import {useClient, useDictionary, useItem, useItems} from '@adamlip/okeoke.client';
import {Checkbox, Divider, Textarea} from '@heroui/react';
import Icon from 'assets/icons';
import BarSoftButton from 'components/_NewComponents/BarSoftButton';
import RenderBarSoftImage from 'components/_NewComponents/RenderBarSoftImage';
import FormattedNumber from 'components/FormattedNumber';
import ModifierGroupSelector from 'components/Modals/OrderItemModalV3/ModifierGroupSelector';
import {AppParamsContext} from 'contexts/AppParamsContext';
import {AnimatePresence, motion} from 'framer-motion';
import {Check, ShoppingCart} from 'lucide-react';
import {useContext, useState} from 'react';
import {Element} from 'react-scroll';

function SelectedMenuSizeConfiguration({itemUUID, selectedSizeUUID, valid, disabled, menuItemParams}) {
	const {assembledItem, setSize, setCount, setModifierCount, setTakeAway, setNotes, getUpsales} = useItem(itemUUID, selectedSizeUUID);
	const {assembledItems} = useItems();
	const {appParams} = useContext(AppParamsContext);
	const {getName} = useClient();
	const {getValue} = useDictionary();
	const [isAdded, setIsAdded] = useState(false);
	const [itemNotes, setItemNotes] = useState('');
	const handleChangeNotes = (e) => setItemNotes(e.target.value);

	const handleBlurNotes = () => setNotes(itemNotes?.slice(0, 100));
	const handleAddToCart = () => {
		if (!isAdded) {
			setIsAdded(true);

			setTimeout(() => {
				setTimeout(() => {
					setIsAdded(false);
					if (menuItemParams?.onAdd) {
						menuItemParams?.onAdd(assembledItem);
					}
				}, 500);
			}, 1000);
		}
	};
	if (assembledItem?.visibleSizes < 1) return null;
	return (
		<div className='flex flex-col gap-2 items-start justify-start w-full h-full overflow-hidden '>
			<div className='w-full flex flex-col gap-0 items-start justify-start  grow overflow-y-auto'>
				<div className='relative w-full'>
					<RenderBarSoftImage uuid={assembledItem?.attributes?.appImage} />

					<div className='w-full flex flex-col gap-0 items-start justify-start px-2 pt-10 absolute bottom-0 z-[50] bg-gradient-to-t from-background to-transparent'>
						<div className='w-full text-center  font-bold text-lg'>{getValue('app_v2_configMainItem')}</div>
						<div className='w-full text-center  text-sm opacity-70'>{getValue('app_v2_configMainItemDesc')}</div>
					</div>
				</div>
				<div className='font-bold text-lg px-2'>{getName(assembledItem?.name)}</div>
				<div className='grow w-full px-2'>{!appParams?.items?.hideDescription && <div className='line-clamp-3   opacity-70 font-light text-xs'>{getName(assembledItem?.description)}</div>}</div>
				<div className='text-orderItemPrice font-medium px-2'>{menuItemParams?.price != null ? <FormattedNumber value={+menuItemParams?.price + +assembledItems?.[assembledItem?.uuid]?.depositPrice} /> : <FormattedNumber value={assembledItems?.[assembledItem?.uuid]?.totalPriceUnit || 0} />}</div>
				{assembledItem?.modifierGroups
					?.filter((group) => group?.availability?.visible === 1)
					.sort((a, b) => (a?.min > b?.min ? 1 : -1))
					.sort((a, b) => (a?.idx > b?.idx ? 1 : -1))
					.map((group, index) => {
						return (
							<Element key={group.uuid} name={group.uuid} className='w-full px-2'>
								<ModifierGroupSelector key={group.uuid} firstInvalid={null} group={group} setModifierCount={setModifierCount} readOnly={false} tryedToAdd={false} name={group?.uuid} />
							</Element>
						);
					})}
				{!(appParams?.items?.takeAwayHidden === true) && (
					<div className='my-2 w-full px-2'>
						<Checkbox isSelected={assembledItem.takeAway} onValueChange={() => setTakeAway(assembledItem.takeAway ? 0 : 1)}>
							<div className='flex flex-row gap-2 items-center'>
								<div className='w-6 h-6'>
									<Icon name='takeAway' className='fill-current' />
								</div>
								{getValue('app_v2_orderItem_takeAway')}
							</div>
						</Checkbox>
					</div>
				)}

				{!(appParams?.items?.notesHidden === true) && (
					<div className='w-full font-small px-2'>
						<Divider className='my-4 bg-border' />
						<Textarea
							aria-label='input'
							type='text'
							classNames={{
								inputWrapper: 'bg-inputBackground text-inputText',
								label: `${appParams?.styles?.fonts?.capitalize ? 'font-bold uppercase' : ''} group-data-[filled-within=true]:text-inputText text-inputText group-data-[filled-within=true]:text-[12px]`,
							}}
							name='itemNotes'
							label={getValue('app_v2_orderItem_notes')}
							value={itemNotes}
							fullWidth={true}
							onChange={handleChangeNotes}
							onBlur={handleBlurNotes}
						/>
					</div>
				)}
			</div>
			<div className={` ${appParams?.buttons?.['addToBasket']?.containerClass || 'grow overflow-hidden'} w-full px-2 shrink-0`}>
				<AnimatePresence>
					<BarSoftButton onPress={handleAddToCart} forceHideStartIcon forceCustom={isAdded} color={isAdded ? 'success' : 'primary'} buttonName='addToBasket' fullWidth isDisabled={assembledItem?.valid ? false : true} size='lg' className=' px-3 justify-start'>
						<AnimatePresence mode='wait'>
							<motion.div key={isAdded ? 'check' : 'cart'} initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} exit={{opacity: 0, y: -20}} transition={{duration: 0.2}} className='flex items-center w-full gap-2'>
								{isAdded ? (
									<>
										<motion.div
											initial={{scale: 0}}
											animate={{scale: 1}}
											transition={{
												type: 'spring',
												stiffness: 500,
												damping: 15,
												delay: 0.1,
											}}>
											<Check className='w-5 h-5' />
										</motion.div>
										<span>{getValue('app_v2_item_saved')}</span>
									</>
								) : (
									<>
										<motion.div
											animate={{rotate: [0, 10, -10, 0]}}
											transition={{
												duration: 0.5,
												ease: 'easeInOut',
												times: [0, 0.2, 0.8, 1],
												repeat: 0,
												repeatDelay: 1,
											}}>
											<ShoppingCart className='w-5 h-5' />
										</motion.div>
										<div className={`w-full flex flex-row gap-2 items-center justify-between overflow-hidden`}>
											<span className='font-semibold'>{getValue('app_v2_configMainItemButton')}</span>
										</div>
									</>
								)}
							</motion.div>
						</AnimatePresence>

						{isAdded && <motion.div initial={{scale: 0, opacity: 0.7}} animate={{scale: 2, opacity: 0}} transition={{duration: 0.7}} className='absolute w-full h-full bg-white rounded-full' />}
					</BarSoftButton>
				</AnimatePresence>
			</div>
		</div>
	);
}

export default SelectedMenuSizeConfiguration;
